<template>
  <q-tab-pane name="invoicing">
    <!-- Invoice Number -->
    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('invoice_number') }}</h6>
        <p class="text-faded">{{ $t('invoice_number_hint') }}</p>
      </div>

      <div class="col-md-9 col-12">
        <!-- Invoice Number Format -->
        <q-field
          :label="$t('invoice_number_format')"
          :labelWidth="3"
          :error="errors.has('invoiceNumberFormat')"
          :error-label="errors.first('invoiceNumberFormat')"
        >
          <q-input
            v-model="formData.invoiceNumberFormat"
            v-validate.disable="'required|min:1|max:255'"
            name="invoiceNumberFormat"
            :placeholder="$t('invoice_number_format')"
          />

          <div class="row items-center q-mt-xs">
            <q-tooltip
              v-html="$t('invoice_number_format_hint') + invoiceNumberFormatExample"
              anchor="top left"
            >
            </q-tooltip>

            <q-icon name="info" color="grey-5"></q-icon>
            <span class="q-ml-xs text-grey-5">{{ $tc('placeholder', 2) }}</span>
          </div>
        </q-field>

        <!-- Current Invoice Number -->
        <q-field
          :label="$t('next_invoice_number')"
          :labelWidth="3"
          :error="errors.has('currentInvoiceNumber')"
          :error-label="errors.first('currentInvoiceNumber')"
        >
          <q-input
            v-model="formData.currentInvoiceNumber"
            v-validate.disable="'required|min:1|max:255'"
            name="currentInvoiceNumber"
            :placeholder="$t('next_invoice_number')"
          />
        </q-field>

        <!-- Invoice number reset interval -->
        <q-field
          :label="$t('reset_invoice_numbers')"
          :label-width="3"
        >
          <sequential-number-reset-input v-model="formData.resetInvoiceNumbers"/>
        </q-field>

        <!-- Quote Number Format -->
        <q-field
          :label="$t('quote_number_format')"
          :labelWidth="3"
          :error="errors.has('quoteNumberFormat')"
          :error-label="errors.first('quoteNumberFormat')"
        >
          <q-input
            v-model="formData.quoteNumberFormat"
            v-validate.disable="'required|min:1|max:255'"
            name="quoteNumberFormat"
            :placeholder="$t('quote_number_format')"
          />

          <div class="row items-center q-mt-xs">
            <q-tooltip
              v-html="$t('quote_number_format_hint') + quoteNumberFormatExample"
              anchor="top left"
            >
            </q-tooltip>

            <q-icon name="info" color="grey-5"></q-icon>
            <span class="q-ml-xs text-grey-5">{{ $tc('placeholder', 2) }}</span>
          </div>
        </q-field>

        <!-- Current Quote Number -->
        <q-field
          :label="$t('next_quote_number')"
          :labelWidth="3"
          :error="errors.has('currentInvoiceNumber')"
          :error-label="errors.first('currentInvoiceNumber')"
        >
          <q-input
            v-model="formData.currentQuoteNumber"
            v-validate.disable="'required|min:1|max:255'"
            name="currentQuoteNumber"
            :placeholder="$t('next_quote_number')"
          />
        </q-field>

        <!-- Quote number reset interval -->
        <q-field
          :label="$t('reset_quote_numbers')"
          :label-width="3"
        >
          <sequential-number-reset-input v-model="formData.resetQuoteNumbers"/>
        </q-field>
      </div>
    </div>

    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('account_system') }}</h6>
        <p class="text-faded">{{ $t('account_system_hint') }}</p>
      </div>

      <div class="col-md-9 col-12">
        <q-field
          :label="$t('account_system')"
          :labelWidth="3"
          :error="errors.has('currentInvoiceNumber')"
          :error-label="errors.first('currentInvoiceNumber')"
        >
          <q-select
            v-model="formData.skrCodes"
            name="accountSystem"
            :options="accountSystemOptions"
            :placeholder="$t('account_system')"
          />
        </q-field>
      </div>
    </div>

    <div class="row justify-end">
      <q-btn type="submit" color="primary" @click="handleSave">{{ $t('save') }}</q-btn>
    </div>

    <hr/>

    <!-- Invoice import -->
    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('capture_imported_invoice') }}</h6>
        <p v-html="$t('import_invoice_hint')" class="text-faded"></p>
      </div>
      <div class="col-md-9 col-12 self-center">
        <q-btn
          :to="{ name: 'pending-invoice-imports', params: { teamId: this.activeTeam.id } }"
          color="primary"
        >
          {{ $t('import_invoices_label') }}
        </q-btn>
      </div>
    </div>

    <hr/>

    <!-- Invocie PDF template -->
    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('pdf_template') }}</h6>
        <p v-html="$t('pdf_template_hint')" class="text-faded"></p>
      </div>

      <div v-if="!team.settings.pdf_template_set" class="col-md-9 col-12 self-center">
        <p class="text-faded">{{ $t('no_pdf_template') }}</p>
        <q-btn color="primary" @click="handlePdfTemplateUploadClick">
          + {{ $t('pdf_template') }}
        </q-btn>

        <input type="file" ref="templateInput" class="hidden file-input" accept="application/pdf"
               @change="handlePdfTemplateUpload">
      </div>

      <div v-else class="col-md-9 col-12 self-center">
        <q-btn color="danger" outline @click="handleDeletePdfTemplate">
          {{ $t('pdf_template_delete_button') }}
        </q-btn>

        <q-btn class="q-ml-md" color="primary" flat @click="handleViewPdfTemplatePreview">
          {{ $t('view_preview') }}
        </q-btn>
      </div>
    </div>

    <hr/>

    <!-- Hidden section -->
    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('pdf_hidden_sections') }}</h6>
        <p v-html="$t('pdf_hidden_sections_hint')" class="text-faded"></p>
      </div>

      <div class="col-md-9 col-12">
        <pdf-hidden-sections v-model="hiddenPdfSections"/>
      </div>
    </div>

    <div class="row justify-end">
      <q-btn type="submit" color="primary" @click="handleHiddenPdfSectionsSave">{{ $t('save') }}</q-btn>
    </div>
  </q-tab-pane>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import FileSaver from 'file-saver'
  import PdfHiddenSections from 'components/Team/Settings/PdfHiddenSections.vue'
  import SequentialNumberResetInput from 'components/Team/Settings/SequentialNumberResetInput.vue'
  import { TeamClient } from 'src/api/Teams'
  import { accounting } from 'src/config'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'
  import i18n from 'src/i18n/i18n'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        formData: {
          invoiceNumberFormat: '',
          currentInvoiceNumber: '',
          quoteNumberFormat: '',
          currentQuoteNumber: '',
          resetInvoiceNumbers: '',
          resetQuoteNumbers: '',
          skrCodes: ''
        },
        pdfTemplate: null,
        hiddenPdfSections: []
      }
    },

    computed: {
      ...mapGetters(['team', 'activeTeam']),

      invoiceNumberFormatExample () {
        return `${(new Date()).getFullYear()}-${this.formData.currentInvoiceNumber}`
      },

      quoteNumberFormatExample () {
        return `${(new Date()).getFullYear()}-${this.formData.currentQuoteNumber}`
      },

      accountSystemOptions () {
        return accounting.accounting_system.map(e => {
          return {
            label: i18n.t(`account_system_${e}`),
            value: e
          }
        })
      }
    },

    methods: {
      ...mapActions(['updateTeam', 'storePdfTemplate', 'deletePdfTemplate']),

      handleSave () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return
            }

            this.storeChanges()
          })
      },

      storeChanges () {
        this.updateTeam({
          teamId: this.team.id,
          team: changeCase.snake(this.formData)
        })
          .then(() => {
            this.$q.notify({ message: this.$t('team_settings_save_success'), type: 'positive' })
          })
          .catch(createErrorFromResponse)
      },

      handlePdfTemplateUploadClick () {
        this.$refs.templateInput.click()
      },

      handlePdfTemplateUpload (e) {
        if (e.target.files.length < 1) {
          return
        }

        this.pdfTemplate = e.target.files[0]

        this.$validator.validateAll({
          pdfTemplate: e.target.files
        })
          .then(result => {
            if (!result) {
              this.resetPdfTemplateInput()
              this.$q.notify({ message: this.errors.first('pdfTemplate'), type: 'warning' })

              return
            }

            this.storePdfTemplate({ teamId: this.team.id, file: this.pdfTemplate })
              .then(() => this.$q.notify({ message: this.$t('pdf_template_upload_complete'), type: 'positive' }))
              .catch(createErrorFromResponse)
          })
      },

      handleDeletePdfTemplate () {
        this.deletePdfTemplate(this.team.id)
          .then(() => this.$q.notify({ message: this.$t('pdf_template_delete_complete'), type: 'positive' }))
      },

      handleViewPdfTemplatePreview () {
        new TeamClient()
          .previewPDFTemplate(this.team.id)
          .then(response => {
            const pdf = response.data
            const blob = new Blob([pdf], { type: 'application/pdf' })
            FileSaver.saveAs(blob, this.$t('preview') + '.pdf')
          })
          .catch(createErrorFromResponse)
      },

      resetPdfTemplateInput () {
        this.pdfTemplate = null
        this.$refs.templateInput.value = null
      },

      handleHiddenPdfSectionsSave () {
        return new TeamClient()
          .updateSettings(this.team.id, {
            'pdf_settings': {
              'template': this.team.settings.pdf_settings.template,
              'template_settings': Object.assign(this.team.settings.pdf_settings.template_settings, {
                'hidden_sections': this.hiddenPdfSections
              })
            }
          })
          .then(() => this.$q.notify({ message: this.$t('team_settings_save_success'), type: 'positive' }))
      }
    },

    created () {
      this.formData = {
        invoiceNumberFormat: this.team.settings.invoice_number_format,
        currentInvoiceNumber: this.team.settings.current_invoice_number,
        quoteNumberFormat: this.team.settings.quote_number_format,
        currentQuoteNumber: this.team.settings.current_quote_number,
        resetInvoiceNumbers: this.team.settings.reset_invoice_numbers,
        resetQuoteNumbers: this.team.settings.reset_quote_numbers,
        skrCodes: this.team.settings.skr_codes
      }

      const templateSettings = this.team.settings.pdf_settings.template_settings

      if (templateSettings && templateSettings.hasOwnProperty('hidden_sections')) {
        this.hiddenPdfSections = templateSettings.hidden_sections
      }

      this.$validator.attach({ name: 'pdfTemplate', rules: `size:2048` })
    },

    components: {
      SequentialNumberResetInput,
      PdfHiddenSections
    }
  }
</script>
